import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Sparkles, 
  TrendingUp, 
  Target, 
  BarChart3, 
  Shield, 
  Zap,
  ArrowRight,
  Play,
  Star,
  ChevronRight,
  Trophy,
  FileText,
  Building2,
  Lock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, useAuth } from "@/App";

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [communityWinnings, setCommunityWinnings] = useState(null);

  // Smart Start Free target — never causes a render-time black screen.
  // While auth is still hydrating we send the user to /register, which is the
  // safe default; if they ARE actually logged in & verified, RegisterPage's
  // own useEffect will then forward them to /dashboard cleanly.
  const handleStartFree = () => {
    if (!authLoading && user && user.email_verified) {
      navigate("/dashboard");
    } else {
      navigate("/register");
    }
  };

  useEffect(() => {
    fetchCommunityWinnings();
  }, []);

  const fetchCommunityWinnings = async () => {
    try {
      const response = await axios.get(`${API}/community/summary`);
      setCommunityWinnings(response.data);
    } catch (error) {
      console.error("Failed to fetch community winnings");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const strategies = [
    { name: "Hot / Cold Analysis", icon: TrendingUp, description: "Reviews frequently and rarely drawn numbers across historical data" },
    { name: "Frequency Distribution", icon: BarChart3, description: "Analyses overall number occurrence patterns across draws" },
    { name: "Gap Theory", icon: Target, description: "Studies intervals between number appearances for balanced selections" },
    { name: "Even / Odd Balancing", icon: Zap, description: "Optimises the mix of even and odd numbers in each combination" },
    { name: "Historical Pattern Analysis", icon: Sparkles, description: "Reviews past draw patterns and recurring trends" },
    { name: "Sum Range Optimisation", icon: Shield, description: "Targets optimal total sum ranges derived from historical data" },
  ];

  const steps = [
    { 
      step: 1, 
      title: "Choose Your Lottery", 
      description: "Select from Tatts Lotto, OZ Lotto, Weekday Windfall, or Powerball" 
    },
    { 
      step: 2, 
      title: "Let AURA Build Your System", 
      description: "AI generates structured number combinations using historical patterns and 25+ analytical methods" 
    },
    { 
      step: 3, 
      title: "Track and Stay Consistent", 
      description: "Monitor your games, review results, and stay disciplined every week" 
    },
  ];

  return (
    <div className="min-h-screen bg-aura-bg relative overflow-hidden">
      {/* Noise overlay */}
      <div className="noise-overlay fixed inset-0 pointer-events-none" />
      
      {/* Grid background */}
      <div className="grid-bg fixed inset-0 pointer-events-none opacity-50" />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-aura-bg/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="logo" data-testid="logo-link">
            <svg width="34" height="34" viewBox="0 0 100 100" className="glow">
              <defs>
                <radialGradient id="ball-header" cx="30%" cy="30%">
                  <stop offset="0%" stopColor="#444"/>
                  <stop offset="100%" stopColor="#000"/>
                </radialGradient>
              </defs>
              <circle cx="50" cy="50" r="45" fill="url(#ball-header)" />
              <circle cx="50" cy="50" r="18" fill="white"/>
              <text x="50" y="58" textAnchor="middle" fontSize="22" fontWeight="bold" fill="black">8</text>
            </svg>
            <span className="logo-text">AURA</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#how-it-works" className="text-aura-text-secondary hover:text-white transition-colors">How It Works</a>
            <a href="#strategies" className="text-aura-text-secondary hover:text-white transition-colors">Strategies</a>
            <Link to="/login" className="text-aura-text-secondary hover:text-white transition-colors" data-testid="login-link">Login</Link>
            <Button 
              onClick={handleStartFree} 
              className="btn-primary"
              data-testid="get-started-btn"
            >
              Start Free
            </Button>
          </nav>
          <Button 
            onClick={handleStartFree} 
            className="md:hidden btn-primary"
            data-testid="mobile-get-started-btn"
          >
            Start Free
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-section"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-aura-emerald/10 border border-aura-emerald/20 text-aura-emerald text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              <span>Play Smart. Stay Consistent.</span>
            </div>
            
            <h1 className="font-heading text-5xl md:text-7xl font-bold leading-tight mb-6">
              <span className="text-white">Stop Guessing.</span><br />
              <span className="text-aura-emerald">Start</span><span className="text-white"> System Play.</span><br />
              <span className="text-aura-emerald">Manifest</span><br />
              <span className="text-aura-emerald">Responsibly.</span>
            </h1>
            
            <p className="text-xl text-aura-text-secondary max-w-xl mb-8">
              AURA by AI Manifest is an AI-powered lottery assistant designed for structured, consistent play — not random guessing. Track your games, follow a system, and make smarter data-informed decisions every week.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={handleStartFree} 
                className="btn-gold text-lg px-8 py-6"
                data-testid="hero-cta-btn"
              >
                Start Free <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                variant="outline" 
                className="border-white/20 hover:border-white/40 text-white bg-transparent px-8 py-6"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="how-it-works-btn"
              >
                <Play className="mr-2 w-5 h-5" /> See How It Works
              </Button>
            </div>

            <div className="mt-8 flex items-center gap-6 text-sm text-aura-text-muted">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-aura-emerald" />
                <span>Responsible Gambling</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-aura-gold" />
                <span>AI Powered Analytics</span>
              </div>
            </div>
          </motion.div>

          {/* Right Content - 8-Ball */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="relative flex items-center justify-center"
          >
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-aura-glow scale-150 blur-3xl opacity-60" />
              
              {/* 8-Ball */}
              <div className="eight-ball large aura-glow float-animation relative z-10">
                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[100px] h-[100px] bg-white rounded-full flex items-center justify-center text-4xl font-bold text-black font-heading">
                  8
                </span>
              </div>

              {/* Floating particles */}
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 bg-aura-emerald rounded-full"
                  style={{
                    top: `${20 + Math.random() * 60}%`,
                    left: `${10 + Math.random() * 80}%`,
                  }}
                  animate={{
                    y: [0, -20, 0],
                    opacity: [0.3, 1, 0.3],
                  }}
                  transition={{
                    duration: 2 + Math.random() * 2,
                    repeat: Infinity,
                    delay: Math.random() * 2,
                  }}
                />
              ))}
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronRight className="w-8 h-8 text-aura-text-muted rotate-90" />
        </motion.div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-xl text-aura-text-secondary max-w-2xl mx-auto">
              Three simple steps to structured, data-informed lottery play
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="card-aura p-8 text-center group"
                data-testid={`step-card-${item.step}`}
              >
                <div className="w-16 h-16 rounded-full bg-aura-emerald/10 border border-aura-emerald/30 flex items-center justify-center mx-auto mb-6 group-hover:bg-aura-emerald/20 transition-colors">
                  <span className="font-heading text-2xl font-bold text-aura-emerald">{item.step}</span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-aura-text-secondary">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Strategy Engine Section */}
      <section id="strategies" className="py-24 px-6 relative bg-aura-surface/50">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-aura-gold/10 border border-aura-gold/20 text-aura-gold text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              <span>Powered by 25+ Analysis Methods</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              <span className="text-white">Smart Players </span><span className="text-aura-emerald">Follow a System</span>
            </h2>
            <p className="text-xl text-aura-text-secondary max-w-2xl mx-auto">
              AURA analyses historical lottery draw data using 25+ data-informed methods to generate structured, balanced number combinations for Australian lotteries.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {strategies.map((strategy, index) => (
              <motion.div
                key={strategy.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-aura p-6 group cursor-pointer"
                data-testid={`strategy-card-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-aura-emerald/10 border border-aura-emerald/30 flex items-center justify-center flex-shrink-0 group-hover:bg-aura-emerald/20 transition-colors">
                    <strategy.icon className="w-6 h-6 text-aura-emerald" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-white mb-2">{strategy.name}</h3>
                    <p className="text-sm text-aura-text-secondary">{strategy.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Stats Bar */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
              {/* Stat 1 */}
              <div className="p-8 text-center border-b md:border-b-0 md:border-r border-white/10">
                <p className="font-heading text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-600 mb-2" style={{ textShadow: '0 0 30px rgba(168, 85, 247, 0.5)' }}>
                  25+
                </p>
                <p className="text-aura-text-secondary text-sm">Data-Informed Methods</p>
              </div>
              
              {/* Stat 2 */}
              <div className="p-8 text-center border-b md:border-b-0 md:border-r border-white/10">
                <p className="font-heading text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-600 mb-2" style={{ textShadow: '0 0 30px rgba(168, 85, 247, 0.5)' }}>
                  1M+
                </p>
                <p className="text-aura-text-secondary text-sm">Simulated Draw Patterns</p>
              </div>
              
              {/* Stat 3 */}
              <div className="p-8 text-center">
                <p className="font-heading text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-600 mb-2" style={{ textShadow: '0 0 30px rgba(168, 85, 247, 0.5)' }}>
                  4
                </p>
                <p className="text-aura-text-secondary text-sm">Lottery Games Supported</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Community Winnings Section */}
      <section className="py-16 px-6 relative">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            onClick={() => navigate("/winnings")}
            className="cursor-pointer group"
            data-testid="community-winnings-counter"
          >
            <div className="relative p-8 rounded-2xl overflow-hidden">
              {/* Background glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-aura-gold/10 via-aura-emerald/10 to-aura-gold/10 group-hover:from-aura-gold/20 group-hover:via-aura-emerald/20 group-hover:to-aura-gold/20 transition-colors" />
              <div className="absolute inset-0 border border-aura-gold/30 rounded-2xl group-hover:border-aura-gold/50 transition-colors" />
              
              <div className="relative text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-aura-gold/10 border border-aura-gold/20 text-aura-gold text-sm font-medium mb-4">
                  <Trophy className="w-4 h-4" />
                  <span>Live Community Stats</span>
                </div>
                
                {communityWinnings?.has_verified_wins ? (
                  <>
                    <p className="text-aura-text-secondary text-lg mb-2">Total Community Winnings</p>
                    <p className="font-heading text-4xl md:text-6xl font-bold mb-4">
                      <span className="text-aura-gold" style={{ textShadow: '0 0 30px rgba(255, 215, 0, 0.5)' }}>
                        {formatCurrency(communityWinnings.total_winnings)}
                      </span>
                    </p>
                    <p className="text-aura-text-muted text-sm">
                      Verified from AURA-tracked tickets
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-aura-text-secondary text-lg mb-2">Verifying Community Results...</p>
                    <p className="font-heading text-3xl md:text-5xl font-bold mb-4">
                      <span className="text-aura-gold/70" style={{ textShadow: '0 0 20px rgba(255, 215, 0, 0.3)' }}>
                        {communityWinnings ? `${communityWinnings.total_valid_tickets || 0} Games Tracked` : '...'}
                      </span>
                    </p>
                    <p className="text-aura-text-muted text-sm">
                      AURA is cross-checking issued games against official draw results.
                    </p>
                  </>
                )}
                
                <p className="text-sm text-aura-emerald mt-4 group-hover:text-aura-emerald-glow transition-colors">
                  View live community stats →
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Responsible Gambling Section */}
      <section className="py-24 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              Play Smart, Play Responsibly
            </h2>
            <p className="text-xl text-aura-text-secondary max-w-2xl mx-auto">
              AURA is built on responsible gaming principles — structured weekly play, full spending visibility, and data-informed decisions. No false promises.
            </p>
          </motion.div>

          {/* 4-Card Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* Card 1 - Responsible Gaming */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-6 rounded-xl border border-white/10 bg-white/[0.02] hover:border-aura-emerald/30 hover:shadow-[0_0_30px_rgba(16,185,129,0.1)] transition-all duration-300"
            >
              <div className="w-10 h-10 rounded-lg bg-aura-emerald/10 flex items-center justify-center mb-4">
                <FileText className="w-5 h-5 text-aura-emerald" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white mb-2">Responsible Gaming</h3>
              <p className="text-sm text-aura-text-secondary leading-relaxed">
                We promote responsible gambling practices with budget tracking, spending alerts, and clear loss visibility.
              </p>
            </motion.div>

            {/* Card 2 - Australian Compliant */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="p-6 rounded-xl border border-white/10 bg-white/[0.02] hover:border-aura-emerald/30 hover:shadow-[0_0_30px_rgba(16,185,129,0.1)] transition-all duration-300"
            >
              <div className="w-10 h-10 rounded-lg bg-aura-emerald/10 flex items-center justify-center mb-4">
                <Building2 className="w-5 h-5 text-aura-emerald" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white mb-2">Australian Compliant</h3>
              <p className="text-sm text-aura-text-secondary leading-relaxed">
                Built for the Australian market with responsible gaming principles and verified draw-result tracking from trusted lottery sources.
              </p>
            </motion.div>

            {/* Card 3 - Secure & Private */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="p-6 rounded-xl border border-white/10 bg-white/[0.02] hover:border-aura-emerald/30 hover:shadow-[0_0_30px_rgba(16,185,129,0.1)] transition-all duration-300"
            >
              <div className="w-10 h-10 rounded-lg bg-aura-emerald/10 flex items-center justify-center mb-4">
                <Lock className="w-5 h-5 text-aura-emerald" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white mb-2">Secure & Private</h3>
              <p className="text-sm text-aura-text-secondary leading-relaxed">
                Your data is encrypted and secure. We never share your information with third parties.
              </p>
            </motion.div>

            {/* Card 4 - Data-Driven */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="p-6 rounded-xl border border-white/10 bg-white/[0.02] hover:border-aura-emerald/30 hover:shadow-[0_0_30px_rgba(16,185,129,0.1)] transition-all duration-300"
            >
              <div className="w-10 h-10 rounded-lg bg-aura-emerald/10 flex items-center justify-center mb-4">
                <BarChart3 className="w-5 h-5 text-aura-emerald" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white mb-2">Data-Driven System</h3>
              <p className="text-sm text-aura-text-secondary leading-relaxed">
                AURA analyses historical draw data and 1M+ simulated patterns, combining 25+ analytical methods to generate structured, balanced number combinations.
              </p>
            </motion.div>
          </div>

          {/* Disclaimer Block */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="p-8 rounded-2xl bg-black/40 border border-white/5 text-center"
          >
            <p className="text-aura-text-secondary text-sm leading-relaxed mb-4">
              AI Manifest does not predict or guarantee winning numbers. AURA is designed to help users approach lottery play in a more structured, consistent, and informed way using data analysis and tracking tools. Lottery outcomes are determined by random draws. Always gamble responsibly.
            </p>
            <p className="text-aura-text-secondary text-sm leading-relaxed mb-4">
              Lottery is entertainment, not an investment strategy. No win is guaranteed. Never spend more than you can afford to lose.
            </p>
            <p className="text-aura-text-muted text-sm">
              If you or someone you know has a gambling problem, please contact{" "}
              <a href="https://www.gamblinghelponline.org.au" target="_blank" rel="noopener noreferrer" className="text-aura-emerald hover:underline">
                Gambling Help Online
              </a>
              {" "}or call <span className="text-white font-medium">1800 858 858</span>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 px-6 relative">
        <div className="max-w-4xl mx-auto text-center">
          {/* Lottery Logos Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {/* Tatts Lotto */}
              <div className="flex flex-col items-center p-5 rounded-xl bg-white/[0.03] border border-white/10 hover:border-blue-500/30 transition-all group" data-testid="lottery-card-tatts">
                <div className="h-14 md:h-[72px] flex items-center justify-center mb-3">
                  <img 
                    src="/logos/tattslotto.png"
                    alt="Tatts Lotto"
                    className="w-auto h-auto max-w-[110px] md:max-w-[140px] max-h-[46px] md:max-h-[60px] object-contain"
                  />
                </div>
                <h4 className="text-white font-medium text-sm md:text-base">Tatts Lotto</h4>
                <p className="text-aura-text-muted text-xs mt-1">Every Saturday</p>
              </div>
              
              {/* Powerball */}
              <div className="flex flex-col items-center p-5 rounded-xl bg-white/[0.03] border border-white/10 hover:border-blue-500/30 transition-all group" data-testid="lottery-card-powerball">
                <div className="h-14 md:h-[72px] flex items-center justify-center mb-3">
                  <img 
                    src="/logos/powerball.png"
                    alt="Powerball"
                    className="w-auto h-auto max-w-[150px] md:max-w-[195px] max-h-[58px] md:max-h-[76px] object-contain"
                  />
                </div>
                <h4 className="text-white font-medium text-sm md:text-base">Powerball</h4>
                <p className="text-aura-text-muted text-xs mt-1">Every Thursday</p>
              </div>
              
              {/* Oz Lotto */}
              <div className="flex flex-col items-center p-5 rounded-xl bg-white/[0.03] border border-white/10 hover:border-green-500/30 transition-all group" data-testid="lottery-card-ozlotto">
                <div className="h-14 md:h-[72px] flex items-center justify-center mb-3">
                  <img 
                    src="/logos/ozlotto.png"
                    alt="Oz Lotto"
                    className="w-auto h-auto max-w-[110px] md:max-w-[140px] max-h-[46px] md:max-h-[60px] object-contain"
                  />
                </div>
                <h4 className="text-white font-medium text-sm md:text-base">Oz Lotto</h4>
                <p className="text-aura-text-muted text-xs mt-1">Every Tuesday</p>
              </div>
              
              {/* Windfall Lotto */}
              <div className="flex flex-col items-center p-5 rounded-xl bg-white/[0.03] border border-white/10 hover:border-purple-500/30 transition-all group" data-testid="lottery-card-windfall">
                <div className="h-14 md:h-[72px] flex items-center justify-center mb-3">
                  <img 
                    src="/logos/windfall.png"
                    alt="Weekday Windfall"
                    className="w-auto h-auto max-w-[110px] md:max-w-[140px] max-h-[46px] md:max-h-[60px] object-contain"
                    style={{ filter: "drop-shadow(0 0 1px rgba(255,255,255,0.3))" }}
                  />
                </div>
                <h4 className="text-white font-medium text-sm md:text-base">Weekday Windfall</h4>
                <p className="text-aura-text-muted text-xs mt-1">Mon, Wed & Fri</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              <span className="text-white">Ready to </span><span className="text-aura-emerald">Manifest</span><span className="text-white"> Millions?</span>
            </h2>
            <p className="text-xl text-aura-text-secondary mb-8">
              Join thousands of Australian lottery players using AI Powered Analytics
            </p>
            <Button 
              onClick={handleStartFree} 
              className="btn-gold text-lg px-10 py-6"
              data-testid="cta-start-btn"
            >
              Start Free Today <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="logo">
              <svg width="32" height="32" viewBox="0 0 100 100" className="glow">
                <defs>
                  <radialGradient id="ball-footer" cx="30%" cy="30%">
                    <stop offset="0%" stopColor="#444"/>
                    <stop offset="100%" stopColor="#000"/>
                  </radialGradient>
                </defs>
                <circle cx="50" cy="50" r="45" fill="url(#ball-footer)" />
                <circle cx="50" cy="50" r="18" fill="white"/>
                <text x="50" y="58" textAnchor="middle" fontSize="22" fontWeight="bold" fill="black">8</text>
              </svg>
              <span className="logo-text">AI Manifest</span>
            </div>
            <div className="text-center md:text-right">
              <p className="text-sm text-aura-text-muted max-w-xl">
                AURA is a data-informed lottery tracking and number analysis system. It is not affiliated with or operated by The Lott.
                Always gamble responsibly.
              </p>
              <div className="responsible-badge inline-block mt-3">
                Responsible Gambling
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
